/**
 * What a picture is seen through, as numbers every output agrees on.
 *
 * A mask is an outline - a frame, a shape, a text, a drawing - and optionally a
 * soft edge and a fade. The canvas, the SVG proof and the PDF press file each
 * say that their own way (a composited canvas, an SVG clipPath or mask, a PDF
 * clip or soft mask), so the sums that decide how soft and where the fade runs
 * live here, once. The same reason the halo lives in one place.
 *
 * A hard mask never becomes pixels: it is a vector clip in both files. Only a
 * soft edge or a fade is rasterised, at press resolution, into the greyscale
 * soft mask a PDF can carry - transparency, which Ghostscript flattens.
 */

import type { Fade, Mask } from '../model/types';
import type { Matrix } from '../text/place';
import { blur, fill, MOST_PIXELS, polygons, PRESS_PPM, type Halo, type Outline, type Pt } from './halo';

export interface MaskSpec {
  /** The outline, in the picture layer's millimetres. Never empty of meaning: a box mask is a rectangle here. */
  outlines: Outline[];
  /** Holes cut by even-odd rather than by winding. */
  evenOdd?: boolean;
  /** The picture's box, which the fade runs across. */
  box: { x: number; y: number; w: number; h: number };
  /** Softness of the edge, as a Gaussian's standard deviation in millimetres. 0 is hard. */
  feather: number;
  fade: Fade | null;
}

export const IDENTITY: Matrix = [ 1, 0, 0, 1, 0, 0 ];

/** a after b: the point goes through b first. */
export function multiply( a: Matrix, b: Matrix ): Matrix {
  return [
    a[ 0 ] * b[ 0 ] + a[ 2 ] * b[ 1 ],
    a[ 1 ] * b[ 0 ] + a[ 3 ] * b[ 1 ],
    a[ 0 ] * b[ 2 ] + a[ 2 ] * b[ 3 ],
    a[ 1 ] * b[ 2 ] + a[ 3 ] * b[ 3 ],
    a[ 0 ] * b[ 4 ] + a[ 2 ] * b[ 5 ] + a[ 4 ],
    a[ 1 ] * b[ 4 ] + a[ 3 ] * b[ 5 ] + a[ 5 ],
  ];
}

/**
 * A layer's own frame: moved to its corner, turned and mirrored about its
 * middle. The same three steps both sinks' group() writes.
 */
export function frameMatrix( l: { x: number; y: number; w: number; h: number; rotation: number; flipX?: boolean; flipY?: boolean } ): Matrix {
  const cx = l.w / 2;
  const cy = l.h / 2;
  let m: Matrix = [ 1, 0, 0, 1, l.x, l.y ];

  if ( l.rotation ) {
    const a = ( l.rotation * Math.PI ) / 180;
    const cos = Math.cos( a );
    const sin = Math.sin( a );

    m = multiply( m, [ 1, 0, 0, 1, cx, cy ] );
    m = multiply( m, [ cos, sin, -sin, cos, 0, 0 ] );
    m = multiply( m, [ 1, 0, 0, 1, -cx, -cy ] );
  }

  if ( l.flipX || l.flipY ) {
    m = multiply( m, [ 1, 0, 0, 1, cx, cy ] );
    m = multiply( m, [ l.flipX ? -1 : 1, 0, 0, l.flipY ? -1 : 1, 0, 0 ] );
    m = multiply( m, [ 1, 0, 0, 1, -cx, -cy ] );
  }

  return m;
}

/**
 * The mirror a flipped picture puts on its mask, undone.
 *
 * Flipping a photograph in a heart-shaped frame flips the photograph, not the
 * heart - and never mirrors the letters of a text it is seen through.
 */
export function unflip( l: { w: number; h: number; flipX?: boolean; flipY?: boolean } ): Matrix {
  if ( ! l.flipX && ! l.flipY ) return IDENTITY;

  return [ l.flipX ? -1 : 1, 0, 0, l.flipY ? -1 : 1, l.flipX ? l.w : 0, l.flipY ? l.h : 0 ];
}

/** How soft the edge is, in millimetres, from the slider's 0-100 and the picture's size. */
export function featherMm( mask: Pick<Mask, 'feather'> | null | undefined, w: number, h: number ): number {
  if ( ! mask || ! ( mask.feather > 0 ) ) return 0;

  return ( Math.min( 100, mask.feather ) / 100 ) * Math.min( Math.abs( w ), Math.abs( h ) ) * 0.12;
}

/**
 * A picture's own box as the outline, when it has no other.
 *
 * Drawn in by twice the softness, so a soft edge has faded to nothing at the
 * edge of the box instead of being cut off halfway through its fade.
 */
export function boxOutline( w: number, h: number, feather: number ): Outline {
  const inset = Math.min( feather * 2, w / 2 - 0.01, h / 2 - 0.01 );
  const i = Math.max( 0, inset );
  const n = ( v: number ) => Math.round( v * 1e4 ) / 1e4;

  return { d: `M ${ n( i ) } ${ n( i ) } L ${ n( w - i ) } ${ n( i ) } L ${ n( w - i ) } ${ n( h - i ) } L ${ n( i ) } ${ n( h - i ) } Z`, m: IDENTITY };
}

/** Whether a fade does anything. */
export function fades( fade: Fade | null | undefined ): fade is Fade {
  return !! fade && fade.amount > 0;
}

/** Where along the fade a fade starts, 0 to 1: the part before it is solid. */
export function fadeStart( fade: Fade ): number {
  return 1 - Math.min( 100, Math.max( 0, fade.amount ) ) / 100;
}

/**
 * How much of the picture shows at a point of its box, from 0 to 1.
 *
 * u and v are proportions of the box. Linear runs along the angle exactly as a
 * gradient's objectBoundingBox does in SVG; radial is an ellipse the shape of
 * the box, out from the middle.
 */
export function fadeAlpha( fade: Fade, u: number, v: number ): number {
  let t: number;

  if ( fade.kind === 'radial' ) {
    t = Math.hypot( u - 0.5, v - 0.5 ) * 2;
  } else {
    const a = ( fade.angle * Math.PI ) / 180;

    t = ( u - 0.5 ) * Math.cos( a ) + ( v - 0.5 ) * Math.sin( a ) + 0.5;
  }

  const start = fadeStart( fade );

  if ( t <= start ) return 1;
  if ( t >= 1 ) return 0;

  return ( 1 - t ) / ( 1 - start || 1 );
}

/** The two ends of a linear fade in unit-box coordinates, for SVG and the canvas. */
export function fadeLine( fade: Fade ): { x1: number; y1: number; x2: number; y2: number } {
  const a = ( fade.angle * Math.PI ) / 180;
  const dx = Math.cos( a ) / 2;
  const dy = Math.sin( a ) / 2;

  return { x1: 0.5 - dx, y1: 0.5 - dy, x2: 0.5 + dx, y2: 0.5 + dy };
}

/**
 * The mask as pixels: how much shows, 0 to 255, row 0 at the top.
 *
 * @param shape Whether the outline is in the pixels. When the edge is hard the
 *   outline is a vector clip instead, and only the fade is rasterised.
 */
export function renderMask( spec: MaskSpec, shape: boolean, ppm = PRESS_PPM ): Halo | null {
  const box = spec.box;
  let minX = box.x;
  let minY = box.y;
  let maxX = box.x + box.w;
  let maxY = box.y + box.h;

  const shapes = shape ? polygons( { outlines: spec.outlines, dx: 0, dy: 0 } ) : [];

  if ( shape ) {
    for ( const poly of shapes ) {
      for ( const [ x, y ] of poly ) {
        minX = Math.min( minX, x );
        minY = Math.min( minY, y );
        maxX = Math.max( maxX, x );
        maxY = Math.max( maxY, y );
      }
    }

    const pad = spec.feather * 3 + 0.2;

    minX -= pad;
    minY -= pad;
    maxX += pad;
    maxY += pad;
  }

  const bounds = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };

  if ( ! ( bounds.w > 0 ) || ! ( bounds.h > 0 ) ) return null;

  const scale = Math.min( ppm, Math.sqrt( MOST_PIXELS / Math.max( 1e-6, bounds.w * bounds.h ) ) );
  const width = Math.max( 1, Math.ceil( bounds.w * scale ) );
  const height = Math.max( 1, Math.ceil( bounds.h * scale ) );

  let coverage: Float32Array;

  if ( shape ) {
    coverage = fill( shapes.map( ( poly ) => poly.map( ( [ x, y ] ): Pt => [ ( x - bounds.x ) * scale, ( y - bounds.y ) * scale ] ) ), width, height );
    blur( coverage, width, height, spec.feather * scale );
  } else {
    coverage = new Float32Array( width * height ).fill( 1 );
  }

  const alpha = new Uint8Array( width * height );
  const fade = fades( spec.fade ) ? spec.fade : null;

  for ( let y = 0; y < height; y++ ) {
    const v = ( bounds.y + ( y + 0.5 ) / scale - box.y ) / ( box.h || 1 );

    for ( let x = 0; x < width; x++ ) {
      const i = y * width + x;
      let a = Math.min( 1, coverage[ i ] as number );

      if ( fade && a > 0 ) {
        a *= fadeAlpha( fade, ( bounds.x + ( x + 0.5 ) / scale - box.x ) / ( box.w || 1 ), v );
      }

      alpha[ i ] = Math.round( a * 255 );
    }
  }

  return { ...bounds, width, height, alpha };
}

/** Path operators (as pathOps writes them) with every point put through a matrix. */
export function transformOps( ops: string, m: Matrix ): string {
  if ( m[ 0 ] === 1 && m[ 1 ] === 0 && m[ 2 ] === 0 && m[ 3 ] === 1 && m[ 4 ] === 0 && m[ 5 ] === 0 ) return ops;

  const r = ( v: number ) => Math.round( v * 1e4 ) / 1e4;

  return ops
    .split( '\n' )
    .map( ( line ) => {
      const parts = line.trim().split( /\s+/ );
      const verb = parts.pop() ?? '';
      const n = parts.map( Number );
      const out: number[] = [];

      for ( let i = 0; i + 1 < n.length; i += 2 ) {
        const x = n[ i ] as number;
        const y = n[ i + 1 ] as number;

        out.push( r( m[ 0 ] * x + m[ 2 ] * y + m[ 4 ] ), r( m[ 1 ] * x + m[ 3 ] * y + m[ 5 ] ) );
      }

      return out.length ? `${ out.join( ' ' ) } ${ verb }` : verb;
    } )
    .join( '\n' );
}
